/**
 * COMPLETION / approval rules — TS mirror of the Rust engine
 * (`packages/core-rust/src/completion`). The SERVER is the authority (closing,
 * `/api/cohorts/:id/my-completion`, `/completion-report`); this mirror covers:
 *   • self-describing metadata of each criterion (editor info-hints);
 *   • the config tree (`CompletionConfig`) + builders (for the editor);
 *   • a PURE evaluator identical to Rust's (local preview + parity tests);
 *   • student progress selectors (line "Attendance 80% ✓ · …");
 *   • the `academy.completion.<key>` manifests for the feature catalog.
 *
 * Keep IN SYNC with the Rust side: any new criterion gets added to both.
 */

import type { FeatureManifest } from "../country/packs";

// ── Criterion keys (== Rust `completion::criteria`) ─────────────────────────────

export const COMPLETION_CRITERIA = [
  "attendance_pct",
  "challenges_passed",
  "final_project",
  "min_final_grade",
  "manual",
  // B6 · stage progression. Unlocking a "level" speaks the SAME language
  // as approval: hard prerequisite = `all(stage_completed)`, level skip =
  // `any(stage_completed, placement_test)`, payment = `entitlement`. Zero
  // new rule engines.
  "stage_completed",
  "placement_test",
  "entitlement",
  "cohort_active",
] as const;

export type CompletionCriterionKey = (typeof COMPLETION_CRITERIA)[number];

// ── Configuration tree (== Rust serde `CompletionNode`) ────────────────────

export type CompletionNode =
  | { type: "all"; children: CompletionNode[] }
  | { type: "any"; children: CompletionNode[] }
  | { type: "criterion"; key: string; params?: Record<string, unknown> };

export interface CompletionConfig {
  root: CompletionNode;
}

/** Does the node have evaluable content? (`all`/`any` with no children = empty). */
export function isMeaningful(node: CompletionNode): boolean {
  if (node.type === "criterion") {
    return true;
  }
  return node.children.some(isMeaningful);
}

export const criterionNode = (
  key: CompletionCriterionKey,
  params: Record<string, unknown> = {},
): CompletionNode => ({ type: "criterion", key, params });
export const allNode = (...children: CompletionNode[]): CompletionNode => ({
  type: "all",
  children,
});
export const anyNode = (...children: CompletionNode[]): CompletionNode => ({
  type: "any",
  children,
});

/** Legacy grade gate (fallback of an empty config) — exact retro-compat. */
export function legacyConfig(): CompletionConfig {
  return { root: allNode(criterionNode("min_final_grade")) };
}

/**
 * RECOMMENDED template (the one that pre-fills the editor and seeds the demo):
 * **grade ≥ passing  OR  (attendance ≥ 75 % AND final project)**.
 */
export function recommendedDefaultConfig(): CompletionConfig {
  return {
    root: anyNode(
      criterionNode("min_final_grade"),
      allNode(criterionNode("attendance_pct", { min_pct: 75 }), criterionNode("final_project")),
    ),
  };
}

/** Structural type-guard (no type-assertions) of the config tree. */
function isCompletionConfigShape(v: unknown): v is CompletionConfig {
  return (
    typeof v === "object" &&
    v !== null &&
    "root" in v &&
    typeof v.root === "object" &&
    v.root !== null
  );
}

/** Parses the column's jsonb (`null`/`{}`/empty tree → `null`). */
export function parseCompletionConfig(raw: unknown): CompletionConfig | null {
  if (raw == null) {
    return null;
  }
  let value: unknown = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === "{}") {
      return null;
    }
    try {
      value = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!isCompletionConfigShape(value)) {
    return null;
  }
  return isMeaningful(value.root) ? value : null;
}

// ── Self-describing metadata of each criterion (editor info-hints) ─────────

export interface CompletionParamSpec {
  key: string;
  /** Short es-VE label (the UI can localize). */
  label: string;
  // `text` is added by B6: progression criteria reference entities by
  // id (a `stage_key`, an entitlement scope), not quantities.
  type: "percent" | "integer" | "text";
  /** Suggested default value. */
  default?: number;
}

export interface CompletionCriterionMeta {
  key: CompletionCriterionKey;
  /** Short label for progress chips ("Attendance", "Challenges"…). */
  shortLabel: string;
  name: string;
  description: string;
  params: CompletionParamSpec[];
}

export const COMPLETION_CRITERIA_META: Record<CompletionCriterionKey, CompletionCriterionMeta> = {
  attendance_pct: {
    key: "attendance_pct",
    shortLabel: "Asistencia",
    name: "Asistencia mínima",
    description:
      "Exige un porcentaje mínimo de asistencia (sesiones presente o tarde sobre las registradas).",
    params: [
      {
        key: "min_pct",
        label: "Asistencia mínima (%)",
        type: "percent",
        default: 75,
      },
    ],
  },
  challenges_passed: {
    key: "challenges_passed",
    shortLabel: "Desafíos",
    name: "Desafíos aprobados",
    description:
      "Exige aprobar un número de desafíos (tareas del curso que no son el proyecto final). Sin número, exige todos.",
    params: [{ key: "count", label: "Nº de desafíos", type: "integer" }],
  },
  final_project: {
    key: "final_project",
    shortLabel: "Proyecto final",
    name: "Proyecto final aprobado",
    description:
      "Exige aprobar el proyecto final del curso (tarea de tipo proyecto calificada ≥ umbral).",
    params: [],
  },
  min_final_grade: {
    key: "min_final_grade",
    shortLabel: "Nota",
    name: "Nota mínima",
    description:
      "Exige que la nota final ponderada alcance un umbral. Sin umbral usa el del modelo de calificación (aprobado).",
    params: [{ key: "min_pct", label: "Nota mínima (%)", type: "percent" }],
  },
  manual: {
    key: "manual",
    shortLabel: "Instructor",
    name: "Marca del instructor",
    description: "El instructor marca manualmente al alumno como completo.",
    params: [],
  },
  stage_completed: {
    key: "stage_completed",
    shortLabel: "Nivel previo",
    name: "Nivel anterior completado",
    description:
      "Exige haber completado el nivel anterior de la ruta (o uno concreto). Dentro de un grupo «todas», es un prerrequisito duro; dentro de «alguna», una vía entre varias.",
    params: [{ key: "stage_key", label: "Nivel concreto (vacío = el anterior)", type: "text" }],
  },
  placement_test: {
    key: "placement_test",
    shortLabel: "Nivelación",
    name: "Prueba de nivelación aprobada",
    description:
      "Permite saltar al nivel sin cursar el anterior si se aprueba una prueba. Quién aprueba lo decide el servidor al corregir, no el dispositivo.",
    params: [{ key: "min_pct", label: "Nota mínima (%)", type: "percent", default: 70 }],
  },
  entitlement: {
    key: "entitlement",
    shortLabel: "Pago",
    name: "Acceso pagado",
    description:
      "Exige que el alumno tenga derecho de acceso vigente (matrícula al día). Lee la titularidad que ya calcula la academia; no consulta facturas.",
    params: [{ key: "scope", label: "Alcance (stage, track, cohort, course)", type: "text" }],
  },
  cohort_active: {
    key: "cohort_active",
    shortLabel: "Cohorte",
    name: "Cohorte en marcha",
    description: "Exige estar matriculado en una cohorte activa de este nivel.",
    params: [],
  },
};

/**
 * Criteria that only make sense within a TRACK. A cohort's rule editor
 * doesn't offer them (there's no previous stage to require on a standalone
 * course) but a stage's editor does. Mirror of `PROGRESSION_CRITERIA` in Rust.
 */
export const PROGRESSION_CRITERIA: readonly CompletionCriterionKey[] = [
  "stage_completed",
  "placement_test",
  "entitlement",
  "cohort_active",
];

export function completionCriterionMeta(key: string): CompletionCriterionMeta | undefined {
  return (COMPLETION_CRITERIA_META as Record<string, CompletionCriterionMeta>)[key];
}

/** Self-describing manifests of the criteria (`academy.completion.<key>`). */
export function completionCriterionManifests(): FeatureManifest[] {
  return COMPLETION_CRITERIA.map((key) => ({
    key: `academy.completion.${key}`,
    name: COMPLETION_CRITERIA_META[key].name,
    description: COMPLETION_CRITERIA_META[key].description,
    scope: "course" as const,
    status: "beta" as const,
    // Progression criteria hang off their own vertical, not the approval
    // rules: without tracks there's no previous stage to require.
    requires: PROGRESSION_CRITERIA.includes(key)
      ? ["academy.progression"]
      : ["academy.completion_rules"],
  }));
}

// ── PURE evaluator (mirror of Rust `evaluate_config`) ──────────────────────────

export interface EvalContext {
  attendanceRate: number | null;
  challengesTotal: number;
  challengesPassed: number;
  finalProject: boolean | null;
  finalGradeRaw: number;
  passingThreshold: number;
  manualComplete: boolean;
  // ── B6 · progression (ADDITIVE extension; all optional so no existing
  // caller has to change) ─────────────────────────────────────────────────────
  /** `stage_key` of the stages already completed. */
  stagesCompleted?: readonly string[];
  /** `stage_key` of the stage prior to the one being evaluated. */
  previousStageKey?: string | null;
  /** `target_stage_key` of the passed and current placement tests. */
  placementPassed?: readonly string[];
  /** Current entitlements as `"<kind>:<id>"`. */
  entitlements?: readonly string[];
  /** Scope that resolves `"ref": "self"`. */
  selfEntitlementRef?: string | null;
  activeCohort?: boolean;
}

export interface CriterionOutcome {
  met: boolean;
  current: number;
  target: number;
  detail: string;
}

const round1 = (x: number): number => Math.round(x * 10) / 10;

function evalCriterion(
  criterionKey: string,
  params: Record<string, unknown>,
  ctx: EvalContext,
): CriterionOutcome {
  const num = (f: string): number | undefined => {
    const v = params[f];
    return typeof v === "number" ? v : undefined;
  };
  switch (criterionKey) {
    case "attendance_pct": {
      const target = num("min_pct") ?? 75;
      if (ctx.attendanceRate == null) {
        return {
          met: false,
          current: 0,
          target,
          detail: "Sin registro de asistencia",
        };
      }
      const current = round1(ctx.attendanceRate * 100);
      return {
        met: current + 1e-9 >= target,
        current,
        target,
        detail: `Asistencia ${current}% (mín. ${target}%)`,
      };
    }
    case "challenges_passed": {
      const target = num("count") ?? ctx.challengesTotal;
      const current = ctx.challengesPassed;
      return {
        met: current + 1e-9 >= target,
        current,
        target,
        detail: `${current}/${target} desafíos aprobados`,
      };
    }
    case "final_project": {
      if (ctx.finalProject === true) {
        return {
          met: true,
          current: 1,
          target: 1,
          detail: "Proyecto final aprobado",
        };
      }
      if (ctx.finalProject === false) {
        return {
          met: false,
          current: 0,
          target: 1,
          detail: "Proyecto final pendiente",
        };
      }
      return {
        met: false,
        current: 0,
        target: 1,
        detail: "El curso no tiene proyecto final",
      };
    }
    case "min_final_grade": {
      const threshold = num("min_pct") == null ? ctx.passingThreshold : num("min_pct")! / 100;
      const current = round1(ctx.finalGradeRaw * 100);
      const target = round1(threshold * 100);
      return {
        met: Math.min(Math.max(ctx.finalGradeRaw, 0), 1) + Number.EPSILON >= threshold,
        current,
        target,
        detail: `Nota ${current}% (mín. ${target}%)`,
      };
    }
    case "manual": {
      return {
        met: ctx.manualComplete,
        current: ctx.manualComplete ? 1 : 0,
        target: 1,
        detail: ctx.manualComplete
          ? "Marcado como completo por el instructor"
          : "Pendiente de marca del instructor",
      };
    }
    // ── B6 · stage progression ───────────────────────────────────────────────
    case "stage_completed": {
      const wantsPrevious = params.previous === true;
      const targetKey = wantsPrevious
        ? ctx.previousStageKey
        : typeof params.stage_key === "string"
          ? params.stage_key
          : undefined;
      if (targetKey == null) {
        // With no previous stage, the first one in the track is OPEN: an
        // impossible prerequisite would leave the track closed from the start.
        return wantsPrevious
          ? { met: true, current: 1, target: 1, detail: "Primera etapa de la ruta" }
          : { met: false, current: 0, target: 1, detail: "Etapa requerida sin identificar" };
      }
      const met = (ctx.stagesCompleted ?? []).includes(targetKey);
      return {
        met,
        current: met ? 1 : 0,
        target: 1,
        detail: met ? "Etapa previa completada" : "Te falta completar la etapa anterior",
      };
    }
    case "placement_test": {
      const target = num("min_pct") ?? 70;
      const key =
        typeof params.target_stage_key === "string"
          ? params.target_stage_key
          : (ctx.selfEntitlementRef?.replace(/^stage:/, "") ?? undefined);
      const met = key != null && (ctx.placementPassed ?? []).includes(key);
      return {
        met,
        current: met ? target : 0,
        target,
        detail: met
          ? "Prueba de nivelación aprobada"
          : `Prueba de nivelación pendiente (mín. ${target}%)`,
      };
    }
    case "entitlement": {
      const scope = typeof params.scope === "string" ? params.scope : "stage";
      const reference = typeof params.ref === "string" ? params.ref : "self";
      const wanted = reference === "self" ? ctx.selfEntitlementRef : `${scope}:${reference}`;
      const met = wanted != null && (ctx.entitlements ?? []).includes(wanted);
      return {
        met,
        current: met ? 1 : 0,
        target: 1,
        detail: met ? "Acceso pagado" : "Falta completar el pago de esta etapa",
      };
    }
    case "cohort_active": {
      const met = ctx.activeCohort === true;
      return {
        met,
        current: met ? 1 : 0,
        target: 1,
        detail: met
          ? "Matrícula activa en una cohorte en marcha"
          : "Sin cohorte activa para esta etapa",
      };
    }
    default: {
      return {
        met: false,
        current: 0,
        target: 0,
        detail: `criterio desconocido: ${criterionKey}`,
      };
    }
  }
}

export interface CriterionBreakdown {
  key: string;
  met: boolean;
  current: number;
  target: number;
  detail: string;
  params: Record<string, unknown>;
}

export interface CompletionStatus {
  passed: boolean;
  configured: boolean;
  criteria: CriterionBreakdown[];
}

function evalNode(node: CompletionNode, ctx: EvalContext, out: CriterionBreakdown[]): boolean {
  if (node.type === "criterion") {
    const o = evalCriterion(node.key, node.params ?? {}, ctx);
    out.push({ key: node.key, ...o, params: node.params ?? {} });
    return o.met;
  }
  if (node.type === "all") {
    let all = true;
    for (const c of node.children) {
      if (!evalNode(c, ctx, out)) {
        all = false;
      }
    }
    return all;
  }
  // any
  let any = false;
  for (const c of node.children) {
    if (evalNode(c, ctx, out)) {
      any = true;
    }
  }
  return node.children.length > 0 && any;
}

/** Evaluates the config against a context (pure mirror of the Rust engine). */
export function evaluateCompletion(config: CompletionConfig, ctx: EvalContext): CompletionStatus {
  const criteria: CriterionBreakdown[] = [];
  const passed = evalNode(config.root, ctx, criteria);
  return { passed, configured: true, criteria };
}

// ── Progress selectors (for the student / the report) ───────────────────────

export interface CompletionChip {
  key: string;
  shortLabel: string;
  met: boolean;
  /** Already-formatted progress text (e.g. "80%", "3/4"). */
  progress: string;
}

/** Per-criterion chip for the student's progress line. */
export function completionChips(status: CompletionStatus): CompletionChip[] {
  return status.criteria.map((c) => {
    const meta = completionCriterionMeta(c.key);
    let progress: string;
    switch (c.key) {
      case "attendance_pct":
      case "min_final_grade": {
        progress = `${c.current}%`;
        break;
      }
      case "challenges_passed": {
        progress = `${c.current}/${c.target}`;
        break;
      }
      default: {
        progress = c.met ? "✓" : "—";
      }
    }
    return {
      key: c.key,
      shortLabel: meta?.shortLabel ?? c.key,
      met: c.met,
      progress,
    };
  });
}

/** Canonical es-VE line (`"Asistencia 80% ✓ · 3/4 desafíos · Proyecto final ✗"`). */
export function completionProgressLine(status: CompletionStatus): string {
  return completionChips(status)
    .map((c) => `${c.shortLabel} ${c.progress} ${c.met ? "✓" : "✗"}`)
    .join(" · ");
}
